import {
  TextControl,
  PanelBody,
  PanelRow,
  ToolbarGroup,
  Button,
} from '@wordpress/components';

import { __ } from '@wordpress/i18n';
/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import {
  InnerBlocks,
  useBlockProps,
  InspectorControls,
  BlockControls,
} from '@wordpress/block-editor';

import { Component } from '@wordpress/element';
import IconPicker from '@components/HelperControls/IconPicker';
import BiteLinkPanel from '@components/DesignPanel/LinkPanel';
import LinkPicker from '@components/HelperControls/LinkPicker';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @param {Object}   props               Properties passed to the function.
 * @param {Object}   props.attributes    Available block attributes.
 * @param {Function} props.setAttributes Function that updates individual attributes.
 *
 * @return {WPElement} Element to render.
 */

export default function Edit({ attributes, setAttributes, clientId }) {
  const { svgCode, linkUrl, linkTitle, linkTarget } = attributes;

  const onChangeSvgCode = (svgCode) => {
    setAttributes({ svgCode: svgCode });
  };

  // change svg from icon picker
  const callbackFunction = (childData) => {
    onChangeSvgCode(childData);
  };

  // update attribute
  const linkCallback = (link) => {
    setAttributes({ ...link });
  };

  const blockProps = useBlockProps({
    className: `${linkUrl ? 'bf-relative' : ''}`,
  });
  const addCurrentFill = () => {
    let newSvgCode = svgCode;

    // Replace all instances of `fill` attribute with `fill="currentColor"`
    newSvgCode = newSvgCode.replace(/fill="[^"]*"/g, 'fill="currentColor"');

    // If no `fill` attribute exists, add `fill="currentColor"` to <path> or <rect> tags
    if (!newSvgCode.includes('fill=')) {
      newSvgCode = newSvgCode.replace(
        /<path|<rect/g,
        (match) => `${match} fill="currentColor"`
      );
    }

    // Set the updated SVG code with the new `fill` attributes
    setAttributes({ svgCode: newSvgCode });
  };

  const addCurrentStroke = () => {
    let newSvgCode = svgCode;

    // Replace all instances of `stroke` attribute with `stroke="currentColor"`
    newSvgCode = newSvgCode.replace(/stroke="[^"]*"/g, 'stroke="currentColor"');

    // If no `stroke` attribute exists, add `stroke="currentColor"` to <path> or <rect> tags
    if (!newSvgCode.includes('stroke=')) {
      newSvgCode = newSvgCode.replace(
        /<path|<rect/g,
        (match) => `${match} stroke="currentColor"`
      );
    }

    // Set the updated SVG code with the new `stroke` attributes
    setAttributes({ svgCode: newSvgCode });
  };

  // Checks if this block's parent block is the Advanced Button block
  // If so, we want to hide the link settings (since the Advanced Button block already has its own link settings)
  const isAdvancedButtonIcon = () => {
    const parentBlocks = wp.data
      .select('core/block-editor')
      .getBlockParentsByBlockName(clientId, 'blockbite/advanced-button');
    return parentBlocks.length > 0;
  };

  return (
    <div {...blockProps}>
      <span dangerouslySetInnerHTML={{ __html: svgCode }} />
      <InspectorControls>
        <PanelBody
          title={__('Svg Settings', 'blockbite-icon')}
          initialOpen={true}
        >
          <PanelRow>
            <div className="flex flex-col gap-4">
              <fieldset>
                <TextControl
                  label={__('Svg Code', 'blockbite-icon')}
                  value={svgCode}
                  onChange={onChangeSvgCode}
                  help={__(
                    'Add your Svg Code, make sure it starts with <svg ...',
                    'blockbite-icon'
                  )}
                />
              </fieldset>
              <p>
                Replace or add a currentFill or currentStroke so you can color
                your icons with the color palette
              </p>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => addCurrentFill()}>
                  Replace Fill
                </Button>
                <Button variant="secondary" onClick={() => addCurrentStroke()}>
                  Replace Current
                </Button>
              </div>
            </div>
          </PanelRow>
        </PanelBody>
        {!isAdvancedButtonIcon() && (
          <PanelBody title={'Link'} initialOpen={false}>
            <BiteLinkPanel
              parentCallback={linkCallback}
              linkUrl={linkUrl}
              linkTitle={linkTitle}
              linkTarget={linkTarget}
              showLinkTile={false}
            />
          </PanelBody>
        )}
      </InspectorControls>
      <BlockControls group="other">
        <ToolbarGroup>
          <IconPicker
            parentCallback={callbackFunction}
            initialIconCode={svgCode}
          />
          {!isAdvancedButtonIcon() && (
            <LinkPicker
              parentCallback={linkCallback}
              linkUrl={linkUrl}
              linkTitle={linkTitle}
              linkTarget={linkTarget}
              showLinkTile={false}
            />
          )}
        </ToolbarGroup>
      </BlockControls>
    </div>
  );
}
